import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Alert, Spinner,
  Badge, Table, Form, InputGroup, Tab, Tabs
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [allFollowUps, setAllFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied: Admin privileges required');
      setLoading(false);
      return;
    }

    loadDashboardData();
  }, [isAdmin]);

 const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, followUpsData] = await Promise.all([
        adminService.getDashboardStats(),
        adminService.getAllFollowUps()
      ]);
      setStats(statsData);
      setAllFollowUps(followUpsData || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Error loading dashboard: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowUps = async () => {
    try {
      setFollowUpsLoading(true);
      const followUpsData = await adminService.getAllFollowUps();
      setAllFollowUps(followUpsData || []);
    } catch (err) {
      setError('Error loading follow-ups: ' + err.message);
    } finally {
      setFollowUpsLoading(false);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'inprogress': return 'primary';
      case 'scheduled': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getLeadStatusVariant = (status) => {
    switch (status) {
      case 'New': return 'primary';
      case 'Contacted': return 'info';
      case 'Visited': return 'secondary';
      case 'Qualified': return 'warning';
      case 'Closed': return 'success';
      case 'Lost': return 'danger';
      default: return 'secondary';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const dateObj = date?.toDate?.() || new Date(date);
      return dateObj.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const formatRelativeTime = (date) => {
    if (!date) return 'N/A';
    try {
      const dateObj = date?.toDate?.() || new Date(date);
      const now = new Date();
      const diffMs = now - dateObj;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return dateObj.toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  // Filter follow-ups based on search and filters
  const filteredFollowUps = allFollowUps.filter(followUp => {
    const matchesSearch =
      followUp.leadName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      followUp.agentEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      followUp.notes?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || followUp.status === statusFilter;

    const matchesDate = dateFilter === 'all' ||
      (dateFilter === 'today' && isToday(followUp.date)) ||
      (dateFilter === 'upcoming' && isUpcoming(followUp.date)) ||
      (dateFilter === 'overdue' && isOverdue(followUp.date));

    return matchesSearch && matchesStatus && matchesDate;
  });

  const isToday = (date) => {
    const dateObj = date?.toDate?.() || new Date(date);
    const today = new Date();
    return dateObj.toDateString() === today.toDateString();
  };

  const isUpcoming = (date) => {
    const dateObj = date?.toDate?.() || new Date(date);
    const today = new Date();
    return dateObj > today && !isToday(date);
  };

  const isOverdue = (date) => {
    const dateObj = date?.toDate?.() || new Date(date);
    const today = new Date();
    return dateObj < today && !isToday(date);
  };

  if (loading) {
    return (
      <Container className="text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading admin dashboard...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="admin-dashboard">
      <Row className="mb-4">
        <Col>
          <h2>Admin Dashboard</h2>
          <p className="text-muted">Manage your real estate CRM system</p>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row className="mb-4">
        <Col>
          <Card className="admin-card">
            <Card.Body>
              <h5>Quick Actions</h5>
              <div className="d-flex gap-2 flex-wrap">
                <Button as={Link} to="/admin/leads" variant="primary">
                  Manage Leads
                </Button>
                <Button as={Link} to="/admin/agents" variant="success">
                  Manage Agents
                </Button>
                <Button as={Link} to="/admin/create-agent" variant="warning">
                  Create New Agent
                </Button>
                <Button as={Link} to="/admin/roles" variant="info">
                  Roles
                </Button>
                <Button as={Link} to="/admin/teams" variant="secondary">
                  Teams
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={loadFollowUps}
                  disabled={followUpsLoading}
                >
                  {followUpsLoading ? 'Refreshing...' : 'Refresh Follow-ups'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

           {/* New Two-Container Layout */}
      <Row>
        {/* Left Container - Leads Statistics */}
        <Col md={8}>
          <div className="admin-container-left">
            <div className="admin-container-header">
              <h4>Leads Overview</h4>
            </div>
            <div className="admin-stats-grid">
              {/* Total Leads Card */}
              <Card className="admin-card">
                <Card.Body className="text-center">
                  <div className="card-value">{stats?.totalLeads || 0}</div>
                  <div className="card-title">Total Leads</div>
                  <div className="card-description">All leads in the system</div>
                </Card.Body>
              </Card>

              {/* Assigned Leads Card */}
              <Card className="admin-card">
                <Card.Body className="text-center">
                  <div className="card-value">{stats?.assignedLeads || 0}</div>
                  <div className="card-title">Assigned Leads</div>
                  <div className="card-description">Leads assigned to agents</div>
                </Card.Body>
              </Card>

              {/* Unassigned Leads Card */}
              <Card className="admin-card">
                <Card.Body className="text-center">
                  <div className="card-value">{stats?.unassignedLeads || 0}</div>
                  <div className="card-title">Unassigned Leads</div>
                  <div className="card-description">Leads waiting for assignment</div>
                </Card.Body>
              </Card>

              {/* Recent Leads Card */}
              <Card className="admin-card">
                <Card.Body className="text-center">
                  <div className="card-value">{stats?.recentLeads || 0}</div>
                  <div className="card-title">Recent Leads</div>
                  <div className="card-description">Last 7 days</div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Col>

        {/* Right Container - Agents Statistics */}
        <Col md={4}>
          <div className="admin-container-right">
            <div className="admin-container-header">
              <h4>Agents Overview</h4>
            </div>
            
            <Row className="g-2">
              <Col md={6}>
                {/* Total Agents Card */}
                <Card className="admin-card">
                  <Card.Body className="text-center">
                    <div className="card-value">{stats?.totalAgents || 0}</div>
                    <div className="card-title">Total Agents</div>
                    <div className="card-description">All agent accounts</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6}>
                {/* Blocked Agents Card */}
                <Card className="admin-card">
                  <Card.Body className="text-center">
                    <div className="card-value">{stats?.blockedAgents || 0}</div>
                    <div className="card-title">Blocked Agents</div>
                    <div className="card-description">Suspended accounts</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6}>
                {/* Coming Soon Card 1 */}
                <Card className="compact-coming-soon">
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <div className="icon">
                      <i className="bi bi-graph-up"></i>
                    </div>
                    <h6>Performance</h6>
                    <small className="text-muted">Coming Soon</small>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6}>
                {/* Coming Soon Card 2 */}
                <Card className="compact-coming-soon">
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <div className="icon">
                      <i className="bi bi-award"></i>
                    </div>
                    <h6>Top Agents</h6>
                    <small className="text-muted">Coming Soon</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      {/* Main Content with Tabs */}
      <Tabs defaultActiveKey="overview" className="mb-3">
        {/* Overview Tab */}
        <Tab eventKey="overview" title="Overview">
          <Row>
            {/* Lead Status Breakdown */}
            {stats && stats.leadsByStatus && (
              <Col md={6}>
                <Card className="admin-card">
                  <Card.Header>
                    <h5 className="mb-0">Leads by Status</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {Object.entries(stats.leadsByStatus).map(([status, count]) => (
                        <Col md={6} key={status} className="mb-2">
                          <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                            <Badge bg={getLeadStatusVariant(status)}>
                              {status}
                            </Badge>
                            <strong>{count}</strong>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* Follow-up Statistics */}
            <Col md={6}>
              <Card className="admin-card">
                <Card.Header>
                  <h5 className="mb-0">Follow-up Statistics</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6} className="mb-2">
                      <div className="text-center p-3 border rounded">
                        <h4 className="text-primary">{allFollowUps.length}</h4>
                        <small className="text-muted">Total Follow-ups</small>
                      </div>
                    </Col>
                    <Col md={6} className="mb-2">
                      <div className="text-center p-3 border rounded">
                        <h4 className="text-warning">
                          {allFollowUps.filter(f => f.status === 'scheduled').length}
                        </h4>
                        <small className="text-muted">Scheduled</small>
                      </div>
                    </Col>
                    <Col md={6} className="mb-2">
                      <div className="text-center p-3 border rounded">
                        <h4 className="text-success">
                          {allFollowUps.filter(f => f.status === 'completed').length}
                        </h4>
                        <small className="text-muted">Completed</small>
                      </div>
                    </Col>
                    <Col md={6} className="mb-2">
                      <div className="text-center p-3 border rounded">
                        <h4 className="text-danger">
                          {allFollowUps.filter(f => isOverdue(f.date)).length}
                        </h4>
                        <small className="text-muted">Overdue</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* All Follow-ups Tab */}
        <Tab eventKey="followups" title={
          <span>
            All Follow-ups
            {allFollowUps.length > 0 && (
              <Badge bg="primary" className="ms-2">
                {allFollowUps.length}
              </Badge>
            )}
          </span>
        }>
          <Card className="admin-card">
            <Card.Header>
              <Row className="align-items-center">
                <Col md={6}>
                  <h5 className="mb-0">All Follow-ups</h5>
                  <small className="text-muted">
                    Monitor all follow-ups across all leads and agents
                  </small>
                </Col>
                <Col md={6}>
                  <InputGroup size="sm">
                    <Form.Control
                      placeholder="Search by lead, agent, or notes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              {/* Filters */}
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label>Status Filter</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    size="sm"
                  >
                    <option value="all">All Statuses</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="inprogress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label>Date Filter</Form.Label>
                  <Form.Select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    size="sm"
                  >
                    <option value="all">All Dates</option>
                    <option value="today">Today</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="overdue">Overdue</option>
                  </Form.Select>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setDateFilter('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>

              {/* Follow-ups Table */}
              {followUpsLoading ? (
                <div className="text-center">
                  <Spinner animation="border" size="sm" />
                  <p>Loading follow-ups...</p>
                </div>
              ) : filteredFollowUps.length === 0 ? (
                <Alert variant="info" className="text-center">
                  No follow-ups found matching your criteria.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped hover className="admin-table">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        <th>Agent</th>
                        <th>Scheduled Date</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th>Lead Status</th>
                        <th>Last Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFollowUps.map((followUp, index) => (
                        <tr key={followUp.id || index}>
                          <td>
                            <div>
                              <strong>{followUp.leadName || 'N/A'}</strong>
                              <br />
                              <small className="text-muted">
                                {followUp.leadPhone || followUp.leadEmail || ''}
                              </small>
                            </div>
                          </td>
                          <td>
                            <Badge bg="light" text="dark">
                              {followUp.agentEmail || 'Unassigned'}
                            </Badge>
                          </td>
                          <td>
                            <div>
                              {formatDate(followUp.date)}
                              <br />
                              <small className={
                                isOverdue(followUp.date) ? 'text-danger' : 'text-muted'
                              }>
                                {formatRelativeTime(followUp.date)}
                              </small>
                            </div>
                          </td>
                          <td>
                            <Badge bg={getStatusVariant(followUp.status)}>
                              {followUp.status?.toUpperCase() || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <small>
                              {followUp.notes ?
                                (followUp.notes.length > 50
                                  ? `${followUp.notes.substring(0, 50)}...`
                                  : followUp.notes
                                )
                                : 'No notes'
                              }
                            </small>
                          </td>
                          <td>
                            <Badge bg={getLeadStatusVariant(followUp.leadStatus)}>
                              {followUp.leadStatus || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatRelativeTime(followUp.updatedAt || followUp.createdAt)}
                            </small>
                          </td>
                          <td>
                            <Button
                              as={Link}
                              to={`/admin/leads/${followUp.leadId}`}
                              variant="outline-primary"
                              size="sm"
                            >
                              View Lead
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default AdminDashboard;